import { useState } from 'react';
import type { KeyboardEvent } from 'react';

import BeanEmpty from '@/assets/icons/rating-bean-empty.svg';
import BeanFull from '@/assets/icons/rating-bean-full.svg';
import BeanHalf from '@/assets/icons/rating-bean-half.svg';

interface ScorePickerProps {
  value: number | null;
  onChange: (score: number) => void;
  max?: number;
  testIdPrefix?: string;
}

export function ScorePicker({
  value,
  onChange,
  max = 5,
  testIdPrefix = 'score-picker',
}: ScorePickerProps) {
  const [hoverScore, setHoverScore] = useState<number | null>(null);
  const previewScore = hoverScore ?? value;
  const displayScore = previewScore ?? 0;

  const beanFor = (position: number): string => {
    if (displayScore >= position + 1) return BeanFull;
    if (displayScore >= position + 0.5) return BeanHalf;
    return BeanEmpty;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = value ?? 0;
    let next: number | null = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next = Math.min(current + 0.5, max);
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        next = Math.max(current - 0.5, 0.5);
        break;
      case 'Home':
        next = 0.5;
        break;
      case 'End':
        next = max;
        break;
      default:
        return;
    }
    event.preventDefault();
    onChange(next);
  };

  const displayText = displayScore.toFixed(1).replace('.', ',');
  const isPlaceholder = previewScore === null || previewScore === 0;
  const liveAnnouncement =
    value !== null && value > 0 ? `Score ${value.toFixed(1)} out of ${String(max)}` : '';

  return (
    <div
      role="radiogroup"
      aria-label="Score"
      tabIndex={0}
      onMouseLeave={() => setHoverScore(null)}
      onKeyDown={handleKeyDown}
      className="inline-flex items-center gap-4 rounded-md px-1 py-1 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-wolisho-red"
    >
      <span
        className={`text-[3rem] leading-none font-semibold tabular-nums ${
          isPlaceholder ? 'text-wolisho-brown/30' : 'text-wolisho-brown'
        }`}
        data-testid={`${testIdPrefix}-display`}
        aria-hidden="true"
      >
        {displayText}
      </span>
      <span className="sr-only" aria-live="polite">
        {liveAnnouncement}
      </span>
      <div className="flex items-center gap-1">
        {Array.from({ length: max }, (_, position) => {
          const halfScore = position + 0.5;
          const fullScore = position + 1;
          return (
            <div key={position} className="relative h-12 w-10">
              <img
                src={beanFor(position)}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-contain pointer-events-none"
              />
              <button
                type="button"
                role="radio"
                tabIndex={-1}
                aria-checked={value === halfScore}
                aria-label={`${halfScore.toFixed(1)} out of ${String(max)}`}
                data-testid={`${testIdPrefix}-${halfScore.toFixed(1)}`}
                onClick={() => onChange(halfScore)}
                onMouseEnter={() => setHoverScore(halfScore)}
                className="absolute inset-y-0 left-0 w-1/2 cursor-pointer rounded-l touch-manipulation"
              />
              <button
                type="button"
                role="radio"
                tabIndex={-1}
                aria-checked={value === fullScore}
                aria-label={`${fullScore.toFixed(1)} out of ${String(max)}`}
                data-testid={`${testIdPrefix}-${fullScore.toFixed(1)}`}
                onClick={() => onChange(fullScore)}
                onMouseEnter={() => setHoverScore(fullScore)}
                className="absolute inset-y-0 right-0 w-1/2 cursor-pointer rounded-r touch-manipulation"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
